import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, OnDestroy } from '@angular/core';

import type { ICellEditorAngularComp } from 'ag-grid-angular';
import { DateTimeInputComponent } from '@softline/ui-core/date-input';
import { DateTimeColumn } from '../../types/grid';
import { filterEmpty, IsoDateTime, IsoDateTimeTz, TimeZoneService, toArray, toIsoDateTimeTz } from '@softline/core';
import type { ICellEditorParams } from 'ag-grid-enterprise';

@Component({
  standalone: true,
  selector: 'soft-date-time-cell-editor',
  imports: [DateTimeInputComponent],
  styles: `
    :host {
      display: flex;
      width: 100%;
      justify-content: center;
      align-items: center;
    }
  `,
  template: `<soft-date-time-input class="my-0.5" [value]="value()" (valueChange)="onValueChange($event)" #inputComp></soft-date-time-input>`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateTimeCellEditor implements ICellEditorAngularComp, AfterViewInit, OnDestroy {
  private params!: ICellEditorParams;
  private readonly el = inject(ElementRef).nativeElement as HTMLElement;
  private readonly timeZoneService = inject(TimeZoneService);
  private resizeObserver?: ResizeObserver;

  readonly col = signal<DateTimeColumn<object, any> | null>(null);
  readonly value = signal<IsoDateTimeTz | null>(null);

  readonly errors = computed(() => {
    const col = this.col();
    const params = this.params;
    const value = this.value();
    if (!col || !col.validate)
      return [];
    return filterEmpty(toArray(col.validate(value, params.data)));
  })

  onValueChange(value: IsoDateTimeTz | null): void {
    this.value.set(value);
  }

  agInit(params: ICellEditorParams): void {
    this.params = params;
    this.col.set(params['col']);
    this.value.set(this.getInitialValue(params));

    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;
      const popupWrapper = this.el.closest('.ag-popup-editor') as HTMLElement | null;
      if (popupWrapper) {
        popupWrapper.style.width = `${width + 24}px`;
      }
    });
    this.resizeObserver.observe(params.eGridCell);
  }

  refresh(params: ICellEditorParams<any, any>) {
    this.params = params;
    this.col.set(params['col']);
    this.value.set(this.toDateTimeTz(params.value));
  }

  ngOnDestroy(): void {
    this.el.removeEventListener('keydown', this.onKeyDown, true);
    this.resizeObserver?.disconnect();
  }

  isCancelAfterEnd(): boolean {
    return this.errors().length > 0;
  }

  getValue(): any {
    return this.value();
  }

  getInitialValue(params: ICellEditorParams): IsoDateTimeTz | null {
    const eventKey = params.eventKey;

    if (eventKey === 'Backspace') {
      return null;
    }

    return this.toDateTimeTz(params.value);
  }

  afterGuiAttached(): void {
    this.el.addEventListener('keydown', this.onKeyDown, true);
  }

  private toDateTimeTz(value: any): IsoDateTimeTz | null {
    if (!value) return null;
    if (typeof value === 'string' && (value.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(value))) {
      return value as IsoDateTimeTz;
    }
    return toIsoDateTimeTz(value as IsoDateTime, this.timeZoneService.timeZone());
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();

      if (this.errors().length > 0) return;

      const api = this.params.api;
      const colKey = this.params.column;
      const rowIndex = this.params.node.rowIndex ?? 0;
      const allCols = api.getAllDisplayedColumns();
      const currentIndex = allCols.findIndex(c => c === colKey);
      const direction = event.shiftKey ? -1 : 1;
      let nextColIndex = currentIndex + direction;
      let nextRowIndex = rowIndex;

      if (nextColIndex >= allCols.length) {
        nextColIndex = 0;
        nextRowIndex++;
      } else if (nextColIndex < 0) {
        nextColIndex = allCols.length - 1;
        nextRowIndex--;
      }

      this.params.stopEditing();

      if (nextRowIndex >= 0 && nextRowIndex < api.getDisplayedRowCount()) {
        const nextCol = allCols[nextColIndex];
        api.setFocusedCell(nextRowIndex, nextCol);
        api.startEditingCell({
          rowIndex: nextRowIndex,
          colKey: nextCol,
        });
      }
    }
  };

  ngAfterViewInit() {
    setTimeout(() => {
      const input = this.el.querySelector('input') as HTMLInputElement | null;
      input?.focus();
    });
  }

  getValidationErrors(): string[] | null {
    return filterEmpty(this.errors().map(o => o.message));
  }
}
